<script setup lang="ts">
import { type FlightDto } from '@clients/FlightInfoApi';
import { ReserveType } from '@utils/itinerary';
import Trip from '@itinerary/trip/Trip';

const { item, direction } = defineProps<IProps>();
interface IProps {
    item: FlightDto;
    direction: 'in' | 'out';
}
const originDestination = computed(() => {
    if (direction === 'in') {
        return `از مبدا ${item.origin}`;
    } else {
        return `به مقصد ${item.destination}`;
    }
});
const formattedScheduledTime = computed(() =>
    dayConfig.js(item.scheduledTime).format('dddd H:mm')
);
const formattedRealTime = computed(() => {
    if (!item.realTime || item.scheduledTime?.includes(item.realTime)) {
        return '';
    }
    const date = dayConfig.gregory(item.realTime, 'HH:mm:ss').format('H:mm');
    return `زمان واقعی: ${date}`;
});
const counterText = computed(() => {
    if (item.counter) {
        return ` - کانتر ${item.counter}`;
    }
    return '';
});
const logo = computed(() => {
    const { logo } = item;
    return Trip.getLogo(ReserveType.Flight, logo ?? '');
});
</script>

<template>
    <td class="root">
        <div class="logo-container">
            <NuxtImg class="logo" :src="logo" alt="logo" />
            <p class="airline">{{ item.airline }}</p>
            <p class="flight-number">{{ item.flightNumber }}</p>
        </div>
        <div class="body-container">
            <p class="origin-destination">
                {{ originDestination }}
            </p>
            <div class="status" :class="[`status-${item.status}`]">
                {{ item.lastStatus }}{{ counterText }}
            </div>
            <p class="time">
                زمان برنامه‌ای:
                {{ formattedScheduledTime }}
                <template v-if="formattedRealTime">
                    <br class="mobile" />
                    <span class="desktop"> | </span>
                    {{ formattedRealTime }}
                </template>
            </p>
        </div>
    </td>
</template>
<style lang="scss" scoped>
.root {
    display: flex;
    border-radius: 12px;
    border: 1px solid usePalette($p-cool-gray, $p-200);
    .logo-container {
        width: 100px;
        height: 100%;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        .logo {
            width: 48px;
            height: 48px;
        }
        .airline {
            margin: 0;
            @include tg-s($w7, usePalette($p-cool-gray, $p-800));
        }
        .flight-number {
            margin: 0;
            @include tg-s($color: usePalette($p-cool-gray, $p-800));
        }
    }
    .body-container {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        flex-grow: 1;
        padding-left: 8px;
        padding-right: 8px;
        .origin-destination {
            @include tg-m($w7, usePalette($p-cool-gray, $p-800));
            margin: 0 0 4px;
        }
        .status {
            align-self: flex-start;
            border-radius: 16px;
            margin-bottom: 4px;
            padding: 2px 8px;
            background-color: usePalette($p-cool-gray, $p-200);
            @include tg-m($w5, usePalette($p-cool-gray, $p-800));
            &.status-5,
            &.status-6,
            &.status-8,
            &.status-9 {
                background-color: usePalette($p-cyan, $p-200);
                @include tg-m($w5, usePalette($p-cyan, $p-600));
            }
            &.status-0,
            &.status-1,
            &.status-3,
            &.status-12,
            &.status-13 {
                background-color: usePalette($p-purple, $p-200);
                @include tg-m($w5, usePalette($p-purple, $p-600));
            }
            &.status-4,
            &.status-7 {
                background-color: usePalette($p-yellow, $p-200);
                @include tg-m($w5, usePalette($p-yellow, $p-700));
            }
            &.status-2,
            &.status-10 {
                background-color: usePalette($p-red, $p-200);
                @include tg-m($w5, usePalette($p-red, $p-600));
            }
        }
        .time {
            margin: 0 0 4px;
            @include tg-m($w5, usePalette($p-gray, $p-700));
            .mobile {
                @include mobile-only(450px) {
                    display: block;
                }
                display: none;
            }
            .desktop {
                @include desktop-only(451px) {
                    display: inline-block;
                }
                display: none;
            }
        }
    }
}
</style>
